import React, { useState } from 'react';
import { trackFormSubmit } from '../../utils/gtag';
import { ChevronLeft, ChevronRight, Send } from 'lucide-react';
import QuizStep from './QuizStep';
import QuizResult from './QuizResult';

// ── Shared interfaces (exported for use in niche quiz pages) ──────────────────

export interface QuizOption {
  label: string;
  description?: string;
  points: number;
}

export interface QuizQuestion {
  id: string;
  question: string;
  introText?: string;
  options: QuizOption[];
}

export interface QuizTier {
  name: 'Mild' | 'Moderate' | 'Significant';
  tierNumber: 1 | 2 | 3;
  minScore: number;
  blurb: string;
}

export interface QuizConfig {
  niche: string;
  quizSlug: string;
  pageTitle: string;
  pageSubtitle?: string;
  questions: QuizQuestion[];
  tiers: QuizTier[];
}

interface QuizShellProps {
  config: QuizConfig;
}

// ── Component ─────────────────────────────────────────────────────────────────

const QuizShell: React.FC<QuizShellProps> = ({ config }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>(
    Array(config.questions.length).fill(null)
  );
  const [showResult, setShowResult] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const totalSteps = config.questions.length;
  const currentQuestion = config.questions[currentStep];
  const currentAnswer = answers[currentStep];
  const canContinue = currentAnswer !== null;

  const totalScore = answers.reduce((sum: number, pts) => sum + (pts ?? 0), 0);

  // tiers sorted descending by minScore so first match wins
  const sortedTiers = [...config.tiers].sort((a, b) => b.minScore - a.minScore);
  const currentTier =
    sortedTiers.find((t) => totalScore >= t.minScore) ?? config.tiers[config.tiers.length - 1];

  const progressPercent =
    showResult ? 100 : Math.round((currentStep / totalSteps) * 100);

  const handleSelect = (points: number) => {
    const updated = [...answers];
    updated[currentStep] = points;
    setAnswers(updated);
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowResult(true);
    }
  };

  const handleBack = () => {
    if (showResult) {
      setShowResult(false);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (formData: {
    name: string;
    email: string;
    consentMarketing: boolean;
    consentHealth: boolean;
  }) => {
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/quiz-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          niche: config.niche,
          tier: currentTier.tierNumber,
          quiz_slug: config.quizSlug,
          consent_health_data: formData.consentHealth,
          consent_timestamp: new Date().toISOString(),
        }),
      });
      if (!response.ok) throw new Error('Submission failed');
      trackFormSubmit(config.quizSlug, 'quiz');
      setIsComplete(true);
    } catch {
      alert('Something went wrong. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 py-16 px-4">
      <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-[28px] font-heading font-bold text-gray-900 mb-2">
            {config.pageTitle}
          </h1>
          {config.pageSubtitle && (
            <p className="text-gray-500 text-sm">{config.pageSubtitle}</p>
          )}
        </div>

        {/* Progress bar */}
        {!showResult && (
          <div className="mb-6">
            <p
              className="text-sm text-gray-500 mb-2"
              aria-label={`Quiz progress: step ${currentStep + 1} of ${totalSteps}`}
            >
              Step {currentStep + 1} of {totalSteps}
            </p>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-accent-600 transition-all duration-300 ease-out"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        )}

        {/* Content */}
        {showResult ? (
          <QuizResult
            tier={currentTier}
            niche={config.niche}
            quizSlug={config.quizSlug}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            isComplete={isComplete}
          />
        ) : (
          <QuizStep
            question={currentQuestion}
            selectedPoints={currentAnswer}
            onSelect={handleSelect}
          />
        )}

        {/* Navigation */}
        {!isComplete && (
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-100">
            <button
              onClick={handleBack}
              disabled={currentStep === 0 && !showResult}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-bold transition-colors ${
                currentStep === 0 && !showResult
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Previous question</span>
            </button>

            {!showResult && (
              <button
                onClick={handleNext}
                disabled={!canContinue}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-bold transition-colors ${
                  canContinue
                    ? 'bg-accent-600 text-white hover:bg-accent-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <span>Next question</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizShell;
